<script setup lang="ts">
import VueCountdown from '@chenfengyuan/vue-countdown'

interface Props {
  messageType?: 'message_home' | 'message_flyers' | 'message_modal' | 'message_recipes' | 'message_shopping_list'
}

const props = withDefaults(defineProps<Props>(), {
  messageType: 'message_home',
})

const modalStore = useModalStore()
const baseStore = useBaseStore()

// Track if banner is manually hidden (for immediate reactivity)
const manuallyHidden = ref(false)

// Get the promo data from baseStore
const promo = computed(() => baseStore.promos)

// Computed properties for dynamic styling and content
const bannerBackground = computed(() => {
  if (promo.value?.banner_bg_from && promo.value?.bg_from_to) {
    return `linear-gradient(135deg, ${promo.value.banner_bg_from} 0%, ${promo.value.bg_from_to} 100%)`
  }
  return 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ff4757 100%)'
})

const titleText = computed(() => promo.value?.title || '-40% sur tous les abonnements !')
const messageText = computed(() => {
  const message = promo.value?.[props.messageType]
  return message || 'Profitez de notre offre exceptionnelle et économisez sur votre abonnement Glouton Premium. Accès illimité à toutes les fonctionnalités.'
})
const _percentageText = computed(() => {
  if (promo.value?.percentage) {
    const percentage = Number.parseFloat(promo.value.percentage)
    return Math.round(percentage)
  }
  return 40
})
const stickerText = computed(() => promo.value?.sticker || 'PROMOTION LIMITÉE')
const textColor = computed(() => promo.value?.text_color || '#FFFFFF')
const titleColor = computed(() => promo.value?.title_color || '#FFFFFF')
const buttonBg = computed(() => promo.value?.button_bg || '#ffffff')
const buttonTextColor = computed(() => promo.value?.button_text_color || '#ff6b35')
const stickerBg = computed(() => promo.value?.sticker_bg || '#ffffff')
const _stickerTextColor = computed(() => promo.value?.sticker_text_color || '#ff6b35')

// Check if banner should be hidden for this message type
const storageKey = computed(() => {
  if (!promo.value?.code)
    return null
  return `promo_hidden_${promo.value.code}_${promo.value.id}_${props.messageType}`
})

const isHidden = computed(() => {
  // Check if manually hidden first (for immediate reactivity)
  if (manuallyHidden.value)
    return true
  if (!storageKey.value)
    return false
  if (!import.meta.client)
    return false
  return localStorage.getItem(storageKey.value) === 'true'
})

// Show banner if not hidden and user should see promos
const showBanner = computed(() => !isHidden.value && promo.value && baseStore.shouldShowPromos && baseStore.promoStatus === 'valid')

// Calculate time remaining until promo ends
const timeRemaining = computed(() => {
  if (!promo.value?.end_date) {
    // Default to 7 days from now if no end date
    const defaultEndDate = new Date()
    defaultEndDate.setDate(defaultEndDate.getDate() + 7)
    return defaultEndDate.getTime() - Date.now()
  }
  const endDate = new Date(promo.value.end_date)
  const now = Date.now()
  const remaining = endDate.getTime() - now
  return remaining > 0 ? remaining : 0
})

function openPromoModal() {
  modalStore.openPromoBannerModal()
}

function closeBanner() {
  // Set manually hidden to immediately hide the banner
  manuallyHidden.value = true
  // Also save to localStorage for persistence across page reloads
  if (storageKey.value && import.meta.client) {
    localStorage.setItem(storageKey.value, 'true')
  }
}
</script>

<template>
  <Transition name="fade">
    <div v-if="showBanner" class="w-auto relative overflow-hidden rounded-xl shadow-lg" :style="{ background: bannerBackground }">
      <!-- Bouton X pour fermer -->
      <button
        type="button"
        class="absolute top-2 right-2 z-10 p-1 rounded-full hover:bg-white/20 transition-colors flex items-center justify-center"
        aria-label="Fermer"
        @click="closeBanner"
      >
        <UIcon name="i-mdi-close" class="w-5 h-5" :style="{ color: titleColor, opacity: 0.75 }" />
      </button>

      <div class="relative px-6 py-4">
        <div class="flex items-start justify-between">
          <!-- Contenu principal -->
          <div class="flex-1">
            <!-- Badge de promotion -->
            <div class="inline-flex items-center px-3 py-1 rounded-full backdrop-blur-sm mb-2" :style="{ backgroundColor: `${stickerBg}20` }">
              <UIcon name="i-mdi-percent" class="w-4 h-4 mr-1" :style="{ color: textColor }" />
              <span class="text-sm font-semibold uppercase" :style="{ color: textColor }">{{ stickerText }}</span>
            </div>

            <!-- Titre principal -->
            <h2 class="text-2xl font-semibold mb-1" :style="{ color: titleColor }">
              {{ titleText }}
            </h2>

            <!-- Description -->
            <p class="text-sm mb-3 opacity-90" :style="{ color: `${textColor}` }" v-html="messageText" />

            <!-- Bouton et Timer -->
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <!-- Bouton d'action -->
              <div class="order-1 sm:order-1">
                <UButton
                  :style="{
                    backgroundColor: buttonBg,
                    color: buttonTextColor,
                    borderColor: buttonBg,
                  }"
                  variant="solid"
                  class="shadow-lg hover:shadow-xl transition-all duration-200"
                  icon="i-mdi-cart-percent"
                  label="Détails + commander"
                  @click="openPromoModal"
                />
              </div>

              <!-- Compte à rebours -->
              <div v-if="timeRemaining > 0" class="order-2 sm:order-2 hidden sm:flex items-center gap-1.5">
                <VueCountdown
                  v-slot="{ days, hours, minutes, seconds }"
                  :time="timeRemaining"
                >
                  <div class="flex items-center gap-1.5">
                    <!-- Jours -->
                    <div class="flex items-center gap-0.5 bg-white/20 backdrop-blur-sm rounded px-1.5 py-0.5">
                      <span class="text-sm font-bold tabular-nums" :style="{ color: textColor }">{{ String(days).padStart(2, '0') }}</span>
                      <span class="text-xs font-medium opacity-90" :style="{ color: textColor }">j</span>
                    </div>
                    <!-- Heures -->
                    <div class="flex items-center gap-0.5 bg-white/20 backdrop-blur-sm rounded px-1.5 py-0.5">
                      <span class="text-sm font-bold tabular-nums" :style="{ color: textColor }">{{ String(hours).padStart(2, '0') }}</span>
                      <span class="text-xs font-medium opacity-90" :style="{ color: textColor }">h</span>
                    </div>
                    <!-- Minutes -->
                    <div class="flex items-center gap-0.5 bg-white/20 backdrop-blur-sm rounded px-1.5 py-0.5">
                      <span class="text-sm font-bold tabular-nums" :style="{ color: textColor }">{{ String(minutes).padStart(2, '0') }}</span>
                      <span class="text-xs font-medium opacity-90" :style="{ color: textColor }">m</span>
                    </div>
                    <!-- Secondes -->
                    <div class="flex items-center gap-0.5 bg-white/20 backdrop-blur-sm rounded px-1.5 py-0.5">
                      <span class="text-sm font-bold tabular-nums" :style="{ color: textColor }">{{ String(seconds).padStart(2, '0') }}</span>
                      <span class="text-xs font-medium opacity-90" :style="{ color: textColor }">s</span>
                    </div>
                  </div>
                </VueCountdown>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Effet de brillance -->
      <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 animate-pulsar-shine pointer-events-none" />
    </div>
  </Transition>
</template>

<style>
@keyframes shimmer {
  0% {
    transform: translateX(-100%) skewX(-12deg);
  }
  100% {
    transform: translateX(200%) skewX(-12deg);
  }
}

.animate-pulsar-shine {
  animation: shimmer 3s ease-in-out infinite;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
